import React from "react";
import { createPortal } from "react-dom";
import styles from "./DrawerPortal.module.css";

interface DrawerPortalProps {
  children: React.ReactNode;
}

/**
 * Returns (and lazily creates) the singleton DOM node that all drawers portal into.
 * Creating it synchronously avoids the useState+useEffect double-render that causes
 * a blank frame on every drawer open.
 */
function getPortalContainer(): HTMLElement {
  let container = document.getElementById("drawer-portal-container");
  if (!container) {
    container = document.createElement("div");
    container.id = "drawer-portal-container";
    container.style.position = "fixed";
    container.style.top = "0";
    container.style.left = "0";
    container.style.width = "100%";
    container.style.height = "100%";
    container.style.pointerEvents = "none";
    container.style.zIndex = "9999";
    document.body.appendChild(container);
  }
  return container;
}

/**
 * DrawerPortal renders its children at the root level of the DOM to avoid
 * z-index stacking context issues. The portal container is created synchronously
 * on first use so there is no blank render frame between mount and content appearing.
 */
const DrawerPortal: React.FC<DrawerPortalProps> = ({ children }) => {
  // useRef so the container reference is stable across re-renders without triggering them
  const containerRef = React.useRef<HTMLElement | null>(null);
  if (!containerRef.current) {
    containerRef.current = getPortalContainer();
  }

  return createPortal(
    <div className={styles.portalContent}>{children}</div>,
    containerRef.current
  );
};

export default DrawerPortal;
